import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import BaseLayout from './BaseLayout';
import { useAuth } from '../context/AuthContext';
import { buildApiUrl } from '../config/api';
import { apiErrorMessage } from './prescriptionForm';
import ExerciseVideoModal from './ExerciseVideoModal';
import { exerciseVideoLink } from './video';
import {
  CATEGORY_ALL,
  EXERCISES_PER_PAGE,
  PAGE_GAP,
  categoriesOf,
  filterExercises,
  pageWindow,
  paginate,
} from './exerciseBrowser';
import '../styles/ExerciseDatabase.css';

function ExerciseDatabase() {
  const { token } = useAuth();

  const [exercises, setExercises] = useState(null); // null = not loaded yet
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(CATEGORY_ALL);
  const [searchTerm, setSearchTerm] = useState('');

  // The demo being watched, or null. Holding the exercise NAME alongside the url
  // is what lets the player be labelled — an unlabelled dialog tells a screen
  // reader nothing about which of 2,766 exercises it is showing.
  const [demo, setDemo] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const fetchExercises = useCallback(async () => {
    setError(null);
    try {
      // axios, like every other screen. This used bare fetch, whose failure path
      // produced "HTTP error! status: 401" instead of the API's own message.
      const response = await axios.get(buildApiUrl('/exercises'), {
        headers: { Authorization: `Bearer ${token}` },
      });
      setExercises(response.data ?? []);
    } catch (err) {
      setExercises([]);
      setError(apiErrorMessage(err, 'Could not load the exercise library.'));
    }
  }, [token]);

  useEffect(() => {
    fetchExercises();
  }, [fetchExercises]);

  // The library is 1059 rows; recomputing the filter on every render — including
  // each keystroke's re-render — is measurable, so both steps are memoised.
  const categories = useMemo(() => categoriesOf(exercises), [exercises]);
  const filtered = useMemo(
    () => filterExercises(exercises, { search: searchTerm, category: selectedCategory }),
    [exercises, searchTerm, selectedCategory]
  );
  const page = useMemo(
    () => paginate(filtered, currentPage, EXERCISES_PER_PAGE),
    [filtered, currentPage]
  );

  const goToPage = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo(0, 0);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleCategoryChange = (e) => {
    setSelectedCategory(e.target.value);
    setCurrentPage(1);
  };

  if (exercises === null) {
    return (
      <BaseLayout>
        <div className="exercise-library">
          <p className="body-copy">Loading the exercise library…</p>
        </div>
      </BaseLayout>
    );
  }

  return (
    <BaseLayout>
      <div className="exercise-library">
        <header className="library-header">
          <span className="pill">Exercise library</span>
          <h1 className="page-title library-title">Exercise database</h1>
          <p className="meta-copy library-subtitle">
            {exercises.length} exercises across {categories.length - 1} movement categories.
          </p>
        </header>

        {error && (
          <div className="status-error library-error" role="alert">
            <span>{error}</span>
            {/* Refetches rather than reloading the whole page, which is what
                window.location.reload() did. */}
            <button type="button" className="btn-ghost" onClick={fetchExercises}>
              Try again
            </button>
          </div>
        )}

        <div className="card library-controls">
          <div className="control-field">
            <label htmlFor="exercise-search">Search</label>
            <input
              id="exercise-search"
              type="search"
              className="field"
              placeholder="Name or category, e.g. squat"
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </div>

          <div className="control-field">
            <label htmlFor="category-select">Category</label>
            <select
              id="category-select"
              className="field"
              value={selectedCategory}
              onChange={handleCategoryChange}
            >
              {categories.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>

          <p className="meta-copy library-count" role="status" aria-live="polite">
            {page.total === 0
              ? 'No matching exercises'
              : `Showing ${page.firstIndex}–${page.lastIndex} of ${page.total}`}
          </p>
        </div>

        {page.total === 0 ? (
          <div className="callout-panel library-empty">
            <p className="library-empty-text">
              No exercises match that search or category.
            </p>
          </div>
        ) : (
          <div className="card library-table-wrap">
            {/* A real table. This was a grid of divs with a header row of
                <strong> elements, so assistive technology got no row/column
                relationships at all. */}
            <table className="library-table">
              <caption className="sr-only">
                Exercise library, page {page.page} of {page.totalPages}
              </caption>
              <thead>
                <tr>
                  <th scope="col">Exercise</th>
                  <th scope="col">Category</th>
                  <th scope="col">Demo</th>
                </tr>
              </thead>
              <tbody>
                {page.items.map((exercise) => {
                  const video = exerciseVideoLink(exercise);
                  return (
                    // Keyed by id, not array index: with filtering and paging,
                    // index keys make React reuse rows across different exercises.
                    <tr key={exercise.id}>
                      <td className="cell-name">
                        {exercise.name}
                        {exercise.isUnilateral && (
                          <span className="unilateral-tag" title="Unilateral — performed one side at a time">
                            Unilateral
                          </span>
                        )}
                      </td>
                      <td className="cell-category">{exercise.category}</td>
                      <td className="cell-demo">
                        {video ? (
                          // A button, not a link. This was an <a target="_blank">
                          // that handed the person to youtube.com — out of the
                          // app, into the recommendation feed, and back only if
                          // they remembered to come back.
                          <button
                            type="button"
                            className="text-link"
                            onClick={() => setDemo({ url: video, name: exercise.name })}
                          >
                            Watch
                            <span className="sr-only"> {exercise.name} demo</span>
                          </button>
                        ) : (
                          <span className="meta-copy">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {page.totalPages > 1 && (
          <nav className="pagination" aria-label="Exercise library pages">
            <button
              type="button"
              className="btn-ghost page-step"
              onClick={() => goToPage(page.page - 1)}
              disabled={page.page === 1}
            >
              <span aria-hidden="true">←</span>
              <span className="sr-only">Previous page</span>
            </button>

            {/* A window, not every page. At 10 per page the full library rendered
                106 buttons; see pageWindow. */}
            {pageWindow(page.page, page.totalPages).map((entry, index) =>
              entry === PAGE_GAP ? (
                <span key={`gap-${index}`} className="page-gap" aria-hidden="true">
                  …
                </span>
              ) : (
                <button
                  key={entry}
                  type="button"
                  className={`page-number${entry === page.page ? ' active' : ''}`}
                  onClick={() => goToPage(entry)}
                  aria-current={entry === page.page ? 'page' : undefined}
                  aria-label={`Page ${entry}`}
                >
                  {entry}
                </button>
              )
            )}

            <button
              type="button"
              className="btn-ghost page-step"
              onClick={() => goToPage(page.page + 1)}
              disabled={page.page === page.totalPages}
            >
              <span aria-hidden="true">→</span>
              <span className="sr-only">Next page</span>
            </button>
          </nav>
        )}
      </div>

      {demo && (
        <ExerciseVideoModal
          videoUrl={demo.url}
          title={demo.name}
          onClose={() => setDemo(null)}
        />
      )}
    </BaseLayout>
  );
}

export default ExerciseDatabase;
